import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Scroll, Plus, Trash, PencilSimple, Printer } from "@phosphor-icons/react";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SectionHeader, EmptyState, fmtDate } from "./ui";

const BUREAUS = [["equifax", "Equifax"], ["experian", "Experian"], ["transunion", "TransUnion"]];
const emptyBureaus = () => ({ equifax: { to_send: false, sent: false, sent_date: "" }, experian: { to_send: false, sent: false, sent_date: "" }, transunion: { to_send: false, sent: false, sent_date: "" } });
const EMPTY_T = { name: "", category: "", body: "" };
const EMPTY_L = { client_id: "", name: "", body: "", bureaus: emptyBureaus(), follow_up_date: "", status: "Draft" };

export default function Letters() {
  const [tab, setTab] = useState("templates");
  const [templates, setTemplates] = useState([]);
  const [letters, setLetters] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tOpen, setTOpen] = useState(false);
  const [tForm, setTForm] = useState(EMPTY_T);
  const [tEdit, setTEdit] = useState(null);
  const [lOpen, setLOpen] = useState(false);
  const [lForm, setLForm] = useState(EMPTY_L);
  const [lEdit, setLEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [t, l, c] = await Promise.all([api.get("/cc/letter-templates"), api.get("/cc/letters"), api.get("/cc/clients")]);
    setTemplates(t.data); setLetters(l.data); setClients(c.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // templates
  const saveTemplate = async () => {
    if (!tForm.name.trim() || !tForm.body.trim()) return toast.error("Name and body are required");
    setSaving(true);
    try {
      const payload = { name: tForm.name, category: tForm.category, body: tForm.body };
      if (tEdit) await api.put(`/cc/letter-templates/${tEdit}`, payload);
      else await api.post("/cc/letter-templates", payload);
      toast.success("Template saved"); setTOpen(false); setTForm(EMPTY_T); setTEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); } finally { setSaving(false); }
  };
  const delTemplate = async (t) => { if (!window.confirm(`Delete template "${t.name}"?`)) return; try { await api.delete(`/cc/letter-templates/${t.id}`); setTemplates((r) => r.filter((x) => x.id !== t.id)); } catch { toast.error("Failed"); } };

  // client letters
  const setL = (k, v) => setLForm((f) => ({ ...f, [k]: v }));
  const setBureau = (key, field, val) => setLForm((f) => ({ ...f, bureaus: { ...f.bureaus, [key]: { ...f.bureaus[key], [field]: val } } }));
  const openNewLetter = () => { setLEdit(null); setLForm(EMPTY_L); setLOpen(true); };
  const openEditLetter = (l) => { setLEdit(l.id); setLForm({ ...EMPTY_L, ...l, bureaus: { ...emptyBureaus(), ...(l.bureaus || {}) }, follow_up_date: l.follow_up_date || "" }); setLOpen(true); };
  const applyTemplate = (tid) => { const t = templates.find((x) => x.id === tid); if (t) setLForm((f) => ({ ...f, name: f.name || t.name, body: t.body })); };
  const saveLetter = async () => {
    if (!lForm.client_id) return toast.error("Select a client");
    if (!lForm.name.trim() || !lForm.body.trim()) return toast.error("Name and body are required");
    setSaving(true);
    try {
      const payload = { client_id: lForm.client_id, name: lForm.name, body: lForm.body, bureaus: lForm.bureaus, follow_up_date: lForm.follow_up_date || null, status: lForm.status };
      if (lEdit) await api.put(`/cc/letters/${lEdit}`, payload);
      else await api.post("/cc/letters", payload);
      toast.success("Letter saved"); setLOpen(false); setLForm(EMPTY_L); setLEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); } finally { setSaving(false); }
  };
  const delLetter = async (l) => { if (!window.confirm(`Delete letter "${l.name}"?`)) return; try { await api.delete(`/cc/letters/${l.id}`); setLetters((r) => r.filter((x) => x.id !== l.id)); } catch { toast.error("Failed"); } };
  const printLetter = async (l, bureau) => {
    try {
      const res = await api.get(`/cc/letters/${l.id}/print`, { params: bureau ? { bureau } : {}, responseType: "blob" });
      const url = URL.createObjectURL(res.data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open PDF"); }
  };

  return (
    <div data-testid="letters-page">
      <SectionHeader title="Dispute Letters" subtitle="Build reusable templates, attach letters to clients, track bureaus and follow-ups."
        action={tab === "templates" ?
          <Button data-testid="new-template-button" onClick={() => { setTEdit(null); setTForm(EMPTY_T); setTOpen(true); }} className="bg-primary text-primary-foreground hover:bg-[#DBC197] gap-1"><Plus size={18} weight="bold" /> New Template</Button> :
          <Button data-testid="new-letter-button" onClick={openNewLetter} className="bg-primary text-primary-foreground hover:bg-[#DBC197] gap-1"><Plus size={18} weight="bold" /> New Letter</Button>}
      />

      <div className="flex gap-2 mb-5">
        <Button size="sm" variant={tab === "templates" ? "default" : "ghost"} onClick={() => setTab("templates")} data-testid="tab-templates" className={tab === "templates" ? "bg-primary text-primary-foreground" : ""}>Templates</Button>
        <Button size="sm" variant={tab === "letters" ? "default" : "ghost"} onClick={() => setTab("letters")} data-testid="tab-letters" className={tab === "letters" ? "bg-primary text-primary-foreground" : ""}>Client Letters</Button>
      </div>

      {loading ? <div className="text-muted-foreground">Loading…</div> : tab === "templates" ? (
        templates.length === 0 ? <EmptyState icon={Scroll} title="No templates yet" hint="Create a dispute letter template you can reuse for any client." /> : (
          <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5" data-testid="templates-list">
            {templates.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-4 px-4 py-3.5" data-testid={`template-row-${t.id}`}>
                <div className="min-w-0"><div className="font-medium truncate">{t.name}</div><div className="text-xs text-muted-foreground truncate">{t.category || "General"}</div></div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => { setTEdit(t.id); setTForm({ name: t.name, category: t.category || "", body: t.body }); setTOpen(true); }} data-testid={`edit-template-${t.id}`}><PencilSimple size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => delTemplate(t)} className="text-red-300 hover:text-red-200" data-testid={`delete-template-${t.id}`}><Trash size={16} /></Button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        letters.length === 0 ? <EmptyState icon={Scroll} title="No client letters" hint="Attach a dispute letter to a client and track which bureaus you've sent to." /> : (
          <div className="space-y-3" data-testid="letters-list">
            {letters.map((l) => (
              <div key={l.id} className="rounded-xl border border-white/10 bg-zinc-900/50 p-4" data-testid={`letter-row-${l.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{l.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{l.client_name || "—"}{l.follow_up_date ? ` · follow up ${fmtDate(l.follow_up_date)}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" variant="outline" className="gap-1 border-white/15" onClick={() => printLetter(l)} data-testid={`print-letter-${l.id}`}><Printer size={15} /> Print</Button>
                    <Button size="icon" variant="ghost" onClick={() => openEditLetter(l)} data-testid={`edit-letter-${l.id}`}><PencilSimple size={16} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => delLetter(l)} className="text-red-300 hover:text-red-200" data-testid={`delete-letter-${l.id}`}><Trash size={16} /></Button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {BUREAUS.map(([key, label]) => {
                    const b = (l.bureaus || {})[key] || {};
                    const cls = b.sent ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : b.to_send ? "bg-amber-500/15 text-amber-300 border-amber-500/30" : "bg-zinc-500/10 text-zinc-400 border-zinc-600/30";
                    return <span key={key} className={`text-xs rounded-full border px-2.5 py-1 ${cls}`}>{label}: {b.sent ? `sent ${b.sent_date ? fmtDate(b.sent_date) : ""}` : b.to_send ? "to send" : "—"}</span>;
                  })}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Template dialog */}
      <Dialog open={tOpen} onOpenChange={setTOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-2xl max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle>{tEdit ? "Edit Template" : "New Template"}</DialogTitle><DialogDescription className="text-sm text-muted-foreground">Reusable dispute-letter body. Use plain text; each line becomes a paragraph.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input data-testid="template-name-input" value={tForm.name} onChange={(e) => setTForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div><Label>Category</Label><Input value={tForm.category} onChange={(e) => setTForm((f) => ({ ...f, category: e.target.value }))} placeholder="Dispute, Goodwill…" /></div>
            </div>
            <div><Label>Body *</Label><Textarea data-testid="template-body-input" rows={10} value={tForm.body} onChange={(e) => setTForm((f) => ({ ...f, body: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setTOpen(false)}>Cancel</Button><Button data-testid="save-template-button" disabled={saving} onClick={saveTemplate} className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{saving ? "Saving…" : "Save template"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Letter dialog */}
      <Dialog open={lOpen} onOpenChange={setLOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-2xl max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle>{lEdit ? "Edit Letter" : "New Client Letter"}</DialogTitle><DialogDescription className="text-sm text-muted-foreground">Attach a letter to a client and track bureaus + follow-up.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Client *</Label>
                <Select value={lForm.client_id} onValueChange={(v) => setL("client_id", v)}>
                  <SelectTrigger data-testid="letter-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name || c.business_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Start from template</Label>
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger data-testid="letter-template-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Letter name *</Label><Input data-testid="letter-name-input" value={lForm.name} onChange={(e) => setL("name", e.target.value)} /></div>
            <div><Label>Body *</Label><Textarea data-testid="letter-body-input" rows={8} value={lForm.body} onChange={(e) => setL("body", e.target.value)} /></div>
            <div>
              <Label className="mb-2 block">Credit bureaus</Label>
              <div className="space-y-2">
                {BUREAUS.map(([key, label]) => {
                  const b = lForm.bureaus[key] || {};
                  return (
                    <div key={key} className="flex flex-wrap items-center gap-4 rounded-lg border border-white/10 px-3 py-2">
                      <span className="w-24 text-sm font-medium">{label}</span>
                      <label className="flex items-center gap-2 text-xs text-muted-foreground"><Checkbox checked={!!b.to_send} onCheckedChange={(v) => setBureau(key, "to_send", !!v)} data-testid={`bureau-${key}-tosend`} /> To send</label>
                      <label className="flex items-center gap-2 text-xs text-muted-foreground"><Checkbox checked={!!b.sent} onCheckedChange={(v) => setBureau(key, "sent", !!v)} data-testid={`bureau-${key}-sent`} /> Sent</label>
                      <Input type="date" className="h-8 w-40" value={b.sent_date || ""} onChange={(e) => setBureau(key, "sent_date", e.target.value)} data-testid={`bureau-${key}-date`} />
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Follow-up date</Label><Input type="date" value={lForm.follow_up_date || ""} onChange={(e) => setL("follow_up_date", e.target.value)} data-testid="letter-followup-input" /></div>
              <div>
                <Label>Status</Label>
                <Select value={lForm.status} onValueChange={(v) => setL("status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Draft", "Sent", "Resolved"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setLOpen(false)}>Cancel</Button><Button data-testid="save-letter-button" disabled={saving} onClick={saveLetter} className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{saving ? "Saving…" : "Save letter"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
